/**
 * OBR-backed StoragePort.
 *
 * The whole thesis of the OBR branch is that nothing above the StoragePort
 * line changes — the existing CharacterRepository, SessionRepository,
 * RuntimeRepository, CheckpointCommitter and custom-content repositories run
 * unmodified on top of this. So this file implements the SAME StoragePort
 * interface the Electron build implements, just backed by OBR player metadata
 * instead of files.
 *
 * We deliberately import the StoragePort type from the parent project rather
 * than copy it, so the two builds can never drift (project rule: no parallel
 * systems). In the real monorepo this becomes a workspace import.
 */
import type { StoragePort } from './storagePort.ts';
import type { ObrPlayerApi } from './obrSdk.ts';

/**
 * One reverse-DNS key holds our entire subtree. Everything this client owns
 * lives under it so a `setMetadata` never clobbers another extension's keys.
 */
export const DCC_METADATA_KEY = 'com.lordofdisndat.dcc/store';

/** Default player-metadata byte budget. OBR documents a 16kB cap for ROOM
 * metadata; the per-player cap is undocumented, so we pick a conservative
 * budget and FAIL LOUDLY on overflow rather than silently corrupt. This is the
 * #1 real-room risk and the adapter refuses to paper over it. */
export const DEFAULT_MAX_BYTES = 14 * 1024;

export class ObrMetadataBudgetError extends Error {
  constructor(public readonly bytes: number, public readonly maxBytes: number) {
    super(`DCC store is ${bytes}B, over the ${maxBytes}B player-metadata budget`);
    this.name = 'ObrMetadataBudgetError';
  }
}

/** The shape held under DCC_METADATA_KEY. Mirrors the StoragePort surface. */
interface DccStore {
  characters: Record<string, unknown>;
  sessions: Record<string, unknown>;
  journals: Record<string, unknown>;
  runtimes: Record<string, unknown>;
  custom: Record<string, unknown>;
  index: unknown | null;
}

function emptyStore(): DccStore {
  return { characters: {}, sessions: {}, journals: {}, runtimes: {}, custom: {}, index: null };
}

function byteLength(value: unknown): number {
  const json = JSON.stringify(value) ?? 'null';
  // Count UTF-8 bytes, not chars: a character sheet may hold non-ASCII names.
  // TextEncoder exists in every browser and in Node 11+, so one code path
  // serves the extension and the tests. (A Node-only Buffer branch used to sit
  // here; it only typechecked because a sandbox stub declared Buffer as any.)
  return new TextEncoder().encode(json).length;
}

export interface ObrStorageOptions {
  maxBytes?: number;
}

export function createObrStorage(player: ObrPlayerApi, options: ObrStorageOptions = {}): StoragePort {
  const maxBytes = options.maxBytes ?? DEFAULT_MAX_BYTES;

  async function readStore(): Promise<DccStore> {
    const meta = await player.getMetadata();
    const raw = meta[DCC_METADATA_KEY];
    if (raw == null || typeof raw !== 'object') return emptyStore();
    // Deep-clone: getMetadata returns a shallow copy, so nested records are
    // shared by reference. Without an isolated copy, mutating a candidate would
    // write through to live metadata even when a later budget check rejects the
    // write. Clone first, then merge onto a fresh store to fill any missing lane.
    const clone = structuredClone(raw) as Partial<DccStore>;
    return { ...emptyStore(), ...clone } as DccStore;
  }

  async function writeStore(store: DccStore): Promise<void> {
    const bytes = byteLength(store);
    if (bytes > maxBytes) throw new ObrMetadataBudgetError(bytes, maxBytes);
    // Write ONLY our namespaced key. setMetadata spreads at the top level, so
    // sibling extensions' keys are preserved untouched.
    await player.setMetadata({ [DCC_METADATA_KEY]: store });
  }

  /** Read-modify-write a single field of the subtree. The mutation is applied
   * to a candidate and only committed if it passes the budget, so a rejected
   * write leaves NO trace in metadata (no partial persist). */
  async function mutate(fn: (s: DccStore) => void): Promise<void> {
    const candidate = await readStore();
    fn(candidate);
    await writeStore(candidate);
  }

  return {
    async listCharacterIds() {
      return Object.keys((await readStore()).characters);
    },
    async readCharacter(id) {
      return (await readStore()).characters[id] ?? null;
    },
    async writeCharacter(id, data) {
      await mutate((s) => { s.characters[id] = data; });
    },
    async deleteCharacter(id) {
      await mutate((s) => { delete s.characters[id]; });
    },

    async readSession(characterId) {
      return (await readStore()).sessions[characterId] ?? null;
    },
    async writeSession(characterId, data) {
      await mutate((s) => { s.sessions[characterId] = data; });
    },
    async deleteSession(characterId) {
      await mutate((s) => { delete s.sessions[characterId]; });
    },

    async readJournal(characterId) {
      return (await readStore()).journals[characterId] ?? null;
    },
    async writeJournal(characterId, data) {
      await mutate((s) => { s.journals[characterId] = data; });
    },
    async deleteJournal(characterId) {
      await mutate((s) => { delete s.journals[characterId]; });
    },

    async readRuntime(characterId) {
      return (await readStore()).runtimes[characterId] ?? null;
    },
    async writeRuntime(characterId, data) {
      await mutate((s) => { s.runtimes[characterId] = data; });
    },
    async deleteRuntime(characterId) {
      await mutate((s) => { delete s.runtimes[characterId]; });
    },

    async readIndex() {
      return (await readStore()).index ?? null;
    },
    async writeIndex(data) {
      await mutate((s) => { s.index = data; });
    },

    async readCustomContent(name) {
      return (await readStore()).custom[name] ?? null;
    },
    async writeCustomContent(name, data) {
      await mutate((s) => { s.custom[name] = data; });
    },

    async describeLocation() {
      return 'Owlbear Rodeo room (this player’s synced metadata)';
    },
  };
}

/** Exposed for a size gauge in the UI and for tests. */
export function measureStoreBytes(store: unknown): number {
  return byteLength(store);
}
